"use client";

import Link from "next/link";
import { useState, useEffect, useRef, useCallback } from "react";
import { LandingFooter } from "@/components/ui/footer";
import { ContentLockup } from "@/components/ui/content-lockup";
import { PillButton } from "@/components/ui/pill-button";
import AbstractVenturesSvg from "./abstract-ventures";
import GeneralCatalystSvg from "./general-catalyst";
import KhoslaSvg from "./khosla";
import BuckleyVenturesSvg from "./buckley-ventures";
import { FundingTimeline } from "./funding-timeline";
import { SiteNavigation } from "@/components/ui/site-navigation";

export interface JobPost {
  id: string;
  title: string;
  content: string;
  location: string | null;
  /** Ashby hosted job description page (not the apply form). */
  jobUrl: string;
}

interface CareersContentProps {
  jobPosts: JobPost[];
}

export function CareersContent({ jobPosts }: CareersContentProps) {
  return (
    <main className="relative min-h-screen overflow-hidden bg-[#FFFDF0] text-[#0a0a0a]">
      <LightGrid />

      <SiteNavigation isDark={false} activePage="careers" />
      <div className="absolute inset-0 mx-auto border-b border-black/10 h-[820px] overflow-hidden top-0">
        <div className="w-full h-full transition-all duration-300 ease-out hover:scale-[102%] opacity-30 hover:opacity-45">
          <img
            src="/images/landing/careers.svg"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        {/* Gradient overlay to fade out at top */}
        <div
          className="absolute top-0 left-0 right-0 h-[350px] z-10 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(255, 253, 240, 1) 0%, rgba(255, 253, 240, 0) 100%)",
          }}
        />
      </div>
      <div className="pointer-events-none relative z-10 mx-auto flex w-full max-w-5xl flex-col gap-16 -mt-16 md:-mt-0 px-8 pt-6 sm:px-10 sm:pt-12 lg:px-5 xl:max-w-[82rem]">
        <div className="relative">
          <div className="mt-72 -translate-y-16 w-fit border border-black/10 backdrop-blur-lg bg-[#FFFDF0]/50 p-12 md:p-16 pr-10 sm:pr-24 md:pr-32 -ml-12 md:-ml-16 pointer-events-auto">
            <ContentLockup
              light
              label="Work with us"
              title="Join us to reshape the foundations of intelligence."
              description="We're a team combining deep math and AI to build systems that truly reason. Our research creates interpretable, reliable, and scalable foundations for intelligence - and real tools that bring them to life. If you want to help build the future, apply today."
              decodeLabel
            />
            <div className="mt-8">
              <Link href="#work-with-us">
                <PillButton variant="outline" icon="none">
                  Explore Roles
                </PillButton>
              </Link>
            </div>
          </div>
        </div>
        <ContentLockup
          light
          label="Series A"
          title="Backed by the leaders shaping the future of AI."
          description="Our Series A funding comes from world-class investors who believe in the power of deep mathematics and AI to redefine what machines can do."
          className="mt-16 pt-24"
          decodeLabel
          decodeLabelMargin="0px 0px -40% 0px"
        />
        <FundingTimeline />
        <InvestorLogoRow />
      </div>

      {/* Full-width separator - breaks out of any container constraints */}
      <div className="relative left-1/2 right-1/2 -mx-[50vw] w-screen h-px bg-black/10 mt-32" />

      <div className="relative z-10 mx-auto flex w-full max-w-5xl flex-col gap-16 px-8 pt-24 pb-16 sm:px-10 lg:px-5 xl:max-w-[82rem]">
        <HiringSection jobPosts={jobPosts} />
      </div>
      <LandingFooter
        careers={false}
        colorMode="gray"
        className="xl:max-w-[83rem] sm:px-10 px-8"
      />
    </main>
  );
}

function InvestorLogoRow() {
  const logos = [
    { id: "khosla", Component: KhoslaSvg },
    { id: "buckley", Component: BuckleyVenturesSvg },
    { id: "abstract", Component: AbstractVenturesSvg },
    { id: "general-catalyst", Component: GeneralCatalystSvg },
  ];

  return (
    <div className="grid grid-cols-1 gap-y-8 sm:grid-cols-2 sm:gap-y-10 gap-x-6 sm:gap-x-12 md:gap-x-20 lg:gap-24 lg:flex lg:justify-between w-full lg:max-w-none max-w-md items-center text-[#0a0a0a]/80">
      {logos.map(({ id, Component }) => (
        <Component
          key={id}
          className={`h-5 lg:h-6 w-auto ${id === "buckley" ? "-translate-x-1 sm:translate-x-[15px]" : ""}`}
        />
      ))}
    </div>
  );
}

const DECODE_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

function JobTile({ job }: { job: JobPost }) {
  const [isHovered, setIsHovered] = useState(false);
  const [charStates, setCharStates] = useState<
    { char: string; resolved: boolean }[]
  >([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const runDecodeAnimation = useCallback((text: string) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Start with scrambled characters
    setCharStates(
      text.split("").map((char) => ({
        char:
          char === " "
            ? " "
            : (DECODE_CHARS[Math.floor(Math.random() * DECODE_CHARS.length)] ??
              "A"),
        resolved: false,
      }))
    );

    const iterations = 12;
    let currentIteration = 0;

    intervalRef.current = setInterval(() => {
      currentIteration++;

      setCharStates(
        text.split("").map((char, index) => {
          if (char === " ") return { char: " ", resolved: true };

          const progress = currentIteration / iterations;
          const shouldResolve = index / text.length < progress;

          if (shouldResolve) {
            return { char: text[index] ?? "", resolved: true };
          }

          return {
            char:
              DECODE_CHARS[Math.floor(Math.random() * DECODE_CHARS.length)] ??
              "A",
            resolved: false,
          };
        })
      );

      if (currentIteration >= iterations) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setCharStates(text.split("").map((char) => ({ char, resolved: true })));
      }
    }, 80);
  }, []);

  useEffect(() => {
    if (isHovered && job.location) {
      runDecodeAnimation(job.location);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isHovered, job.location, runDecodeAnimation]);

  return (
    <a
      href={job.jobUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center justify-between border border-black/20 bg-transparent p-5 text-sm uppercase text-[#0a0a0a]/60 transition hover:bg-[#F5F3E7] hover:border-black/30"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span className="font-ibm-plex-mono pr-8 text-left font-medium leading-[1.5] tracking-[0.03em] text-[#0a0a0a]">
        {job.title}
      </span>
      {job.location && (
        <span className="font-ibm-plex-mono text-xs tracking-[0.05em] text-[#0a0a0a]/50 whitespace-nowrap">
          {isHovered && charStates.length > 0
            ? charStates.map((state, index) => (
                <span key={index} style={{ opacity: state.resolved ? 1 : 0.5 }}>
                  {state.char}
                </span>
              ))
            : job.location}
        </span>
      )}
    </a>
  );
}

function HiringSection({ jobPosts }: { jobPosts: JobPost[] }) {
  const [locationFilter, setLocationFilter] = useState<string>("all");

  // Get unique locations, sorted: San Francisco first, London second, then alphabetically
  const uniqueLocations = Array.from(
    new Set(jobPosts.map((job) => job.location).filter(Boolean) as string[])
  ).sort((a, b) => {
    const locA = a.toLowerCase();
    const locB = b.toLowerCase();
    if (locA === "san francisco" && locB !== "san francisco") return -1;
    if (locB === "san francisco" && locA !== "san francisco") return 1;
    if (locA === "london" && locB !== "london") return -1;
    if (locB === "london" && locA !== "london") return 1;
    return locA.localeCompare(locB);
  });

  // Filter jobs by selected location
  const filteredJobs =
    locationFilter === "all"
      ? jobPosts
      : jobPosts.filter(
          (job) => job.location?.toLowerCase() === locationFilter.toLowerCase()
        );

  // Split filtered jobs into two columns
  const midpoint = Math.ceil(filteredJobs.length / 2);
  const leftColumn = filteredJobs.slice(0, midpoint);
  const rightColumn = filteredJobs.slice(midpoint);

  return (
    <section id="work-with-us" className="mt-8 scroll-mt-24">
      <div className="relative flex flex-col gap-8">
        <ContentLockup
          light
          label="Apply for San Francisco or London"
          title="Open Roles"
          description="If you've ever been kept awake wondering what will come after deep learning, or simply want to have a hand in building the future of machine learning, we want to hear from you."
          decodeLabel
        />

        {/* Location Filter Toggle */}
        <div className="flex items-center gap-1 flex-wrap">
          <button
            onClick={() => setLocationFilter("all")}
            className={`font-ibm-plex-mono text-xs px-2.5 py-1 rounded transition-all cursor-pointer ${
              locationFilter === "all"
                ? "bg-[#0a0a0a]/10 text-[#0a0a0a]"
                : "text-[#0a0a0a]/40 hover:bg-[#0a0a0a]/5 hover:text-[#0a0a0a]/70"
            }`}
          >
            All
          </button>
          {uniqueLocations.map((location) => (
            <button
              key={location}
              onClick={() => setLocationFilter(location)}
              className={`font-ibm-plex-mono text-xs px-2.5 py-1 rounded transition-all cursor-pointer ${
                locationFilter.toLowerCase() === location.toLowerCase()
                  ? "bg-[#0a0a0a]/10 text-[#0a0a0a]"
                  : "text-[#0a0a0a]/40 hover:bg-[#0a0a0a]/5 hover:text-[#0a0a0a]/70"
              }`}
            >
              {location}
            </button>
          ))}
        </div>

        <div className="relative grid gap-6 pt-2 lg:gap-10 lg:grid-cols-2">
          {[leftColumn, rightColumn].map((column, columnIndex) => (
            <div key={columnIndex} className="space-y-6">
              {column.map((job) => (
                <JobTile key={job.id} job={job} />
              ))}
            </div>
          ))}
        </div>

        {filteredJobs.length === 0 && (
          <p className="text-[#0a0a0a]/50 font-arizona-sans text-center py-8">
            No open positions at this time. Check back soon!
          </p>
        )}
      </div>
    </section>
  );
}

function LightGrid() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-[#FFFDF0]" />
      <div className="absolute inset-0 opacity-[0.1]">
        <GridOverlay opacity={0.1} lineTone="rgba(0,0,0,0.08)" size={72} />
      </div>
    </div>
  );
}

function GridOverlay({
  opacity = 0.2,
  lineTone = "rgba(0,0,0,0.08)",
  size = 64,
}: {
  opacity?: number;
  lineTone?: string;
  size?: number;
}) {
  return (
    <div
      className="size-full"
      style={{
        backgroundImage: `linear-gradient(${lineTone} 1px, transparent 1px), linear-gradient(90deg, ${lineTone} 1px, transparent 1px)`,
        backgroundSize: `${size}px ${size}px`,
        opacity,
      }}
    />
  );
}
